import locale from "@bonliva-crm/locale";
import {
  Box,
  makeStyles,
  InputLabel,
  Typography,
  FormControl,
  Chip
} from "@material-ui/core";
import { useEffect, useState } from "react";
import ReactSelect, {
  components,
  createFilter,
  GroupBase,
  OptionsOrGroups
} from "react-select5";
import { SelectBadge } from "./components/select-badge";
import { CheckboxOptionComponent } from "./components/checkbox-option";
import { ClearIndicatorComponent } from "./components/clear-indicator";
import { DropdownIndicatorComponent } from "./components/dropdown-indicator";
import { PlaceholderComponent } from "./components/placeholder";
import { useSelectStyle } from "./hooks/use-select-style";
import { MultiSelectCommonProps } from "./utils/multi-select-common-props";
import { MenuComponent } from "./components/menu";

export function MultiSelect<Option, Group extends GroupBase<Option> = GroupBase<Option>>(
  props: MultiSelectCommonProps<Option, Group>
) {
  const {
    showDropDownIndicator = true,
    hidePlaceholderOnFocus = true,
    hideController = false,
    menuWidth,
    ...mainProps
  } = props;

  const {
    components: customComponents,
    options: _options,
    inputLabel,
    styleProps,
    showBadge,
    badgeContent,
    showOptionDivider,
    dropDownIndicatorIcon,
    isClearable,
    onClear,
    onChange,
    menuChildren,
    name,
    onMenuOpen,
    selectRef,
    labelShrink,
    ...rest
  } = mainProps;
  const { selectStyle, selectTheme } = useSelectStyle<Option, true, Group>(styleProps);

  const [_selectedAmount, _setSelectedAmount] = useState(0);
  const [isOpen, _setIsOpen] = useState(mainProps.menuIsOpen ?? false);
  const selectedAmount =
    mainProps.value && Array.isArray(mainProps.value)
      ? mainProps.value.length
      : _selectedAmount;

  const classes = useStyles({ menuWidth });

  const [options, setOptions] = useState<OptionsOrGroups<Option, Group> | undefined>(
    _options
  );

  const _customComponents = { ...customComponents };
  if (hideController) {
    _customComponents.Control = () => null;
  }

  useEffect(() => {
    setOptions(_options);
  }, [_options]);

  const hasValue =
    mainProps.value && Array.isArray(mainProps.value) && mainProps.value.length > 0;

  let firstValue =
    Array.isArray(mainProps.value) &&
    mainProps.value[0] &&
    "label" in mainProps.value[0] &&
    mainProps.value[0].label;

  if (!firstValue) {
    firstValue = undefined;
  }

  const badgeContentValue = badgeContent ? badgeContent : firstValue;

  return (
    <Box className={classes.root}>
      {showBadge && (
        <SelectBadge selectedAmount={selectedAmount} text={badgeContentValue} />
      )}

      <FormControl>
        {inputLabel && (
          <InputLabel htmlFor={name} shrink={!!hasValue || isOpen || labelShrink}>
            <Typography
              variant="body1"
              style={{ opacity: mainProps.isDisabled ? 0.5 : 1 }}
            >
              {inputLabel}
            </Typography>
          </InputLabel>
        )}

        <ReactSelect<Option, true, Group>
          // menuIsOpen={isOpen}
          components={{
            Option: (p) =>
              mainProps.optionType === "checkbox" ? (
                CheckboxOptionComponent({
                  ...p,
                  showDivider: showOptionDivider
                })
              ) : (
                <components.Option {...p} />
              ),
            Placeholder: (p) => PlaceholderComponent({ ...p, hidePlaceholderOnFocus }),
            DropdownIndicator: (p) =>
              showDropDownIndicator
                ? DropdownIndicatorComponent({
                    ...p,
                    icon: dropDownIndicatorIcon
                  })
                : null,
            IndicatorSeparator: null,
            ClearIndicator: (p) =>
              isClearable && onClear ? ClearIndicatorComponent({ ...p, onClear }) : <></>,
            MultiValue: (p) => {
              return (
                <Chip
                  size="small"
                  label={(p.data as any).label}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                  onDelete={() => {
                    onChange?.(
                      (mainProps.value as Option[]).filter(
                        (v) => (v as any).value !== (p.data as any).value
                      ),
                      { action: "remove-value", removedValue: p.data }
                    );
                    showBadge &&
                      _setSelectedAmount((mainProps.value as Option[]).length - 1);
                  }}
                />
              );
            },

            Menu: MenuComponent,
            ..._customComponents
          }}
          theme={(theme) => ({
            ...theme,
            ...selectTheme
          })}
          closeMenuOnSelect={true}
          hideSelectedOptions={true}
          isClearable={true}
          backspaceRemovesValue={false}
          noOptionsMessage={() => locale.NoMoreSearchResults}
          loadingMessage={() => ""}
          isMulti={true}
          isSearchable={true}
          styles={selectStyle}
          filterOption={createFilter({ ignoreAccents: false })}
          onChange={(newValue, meta) => {
            onChange && onChange(newValue, meta);
            showBadge && _setSelectedAmount(newValue.length);
          }}
          options={options}
          onMenuClose={() => {
            _setIsOpen(false);
          }}
          onMenuOpen={() => {
            _setIsOpen(true);
            onMenuOpen && onMenuOpen();
            if (mainProps.sortOptionsOnMenuOpenFn) {
              options && setOptions(mainProps.sortOptionsOnMenuOpenFn(options.slice()));
            }
          }}
          // @ts-expect-error recommended way to pass component props in
          // https://react-select.com/components#defining-components
          menuChildren={menuChildren}
          ref={selectRef}
          {...rest} //FIXME !WARNING override all props be careful
        />
      </FormControl>
    </Box>
  );
}

type StyleProps = {
  menuWidth?: string;
};

const useStyles = makeStyles((theme) => ({
  root: (props: StyleProps) => ({
    display: "flex",
    flexDirection: "column",
    rowGap: "0.5rem",
    position: "relative",
    width: props.menuWidth
  })
}));

export default MultiSelect;
